import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  Dialog,
  DialogContent,
  Grid,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import ErrorMessage from "../../common/ErrorMessage/ErrorMessage";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../providers/AuthProvider";
import { testIds } from "./AccountSetupModal.testIds";

type SetupStep = "ORGANIZATION" | "SUBSCRIPTION";

type AccountSetupModalProps = {
  needsSetup: boolean;
  setupStep: SetupStep | null;
  onOrganizationSetup: (orgName: string, dotNumber: string) => void;
  onSubscribe: (subscriptionType: "fleet" | "liquid") => void;
  isSettingUpOrganization: boolean;
  isSubscribing: boolean;
  hasTrial: boolean;
  error: Error | null;
};

const AccountSetupModal = ({
  onOrganizationSetup,
  onSubscribe,
  isSettingUpOrganization,
  isSubscribing,
  hasTrial,
  setupStep,
  needsSetup,
  error,
}: AccountSetupModalProps) => {
  const { t } = useTranslation(["account", "common"]);
  const setupSteps: SetupStep[] = ["ORGANIZATION", "SUBSCRIPTION"];
  const [organizationName, setOrganizationName] = useState("");
  const [dotNumber, setDotNumber] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(needsSetup);
  const { signOut } = useAuth();

  useEffect(() => {
    setIsModalOpen(needsSetup);
  }, [needsSetup]);
  return (
    <Dialog open={isModalOpen} maxWidth="lg">
      <DialogContent>
        <Container maxWidth="md">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info">
                {t(
                  "setupDescription",
                  "Setup your account in order to start using the platform"
                )}
              </Alert>
              <ErrorMessage error={error} />
            </Grid>
            <Grid item sm={2}>
              <Stepper
                activeStep={setupStep ? setupSteps.indexOf(setupStep) : 0}
                orientation="vertical"
              >
                <Step>
                  <StepLabel>
                    {t("setupOrg", "Setup your organization")}
                  </StepLabel>
                </Step>
                <Step>
                  <StepLabel>
                    {hasTrial
                      ? t("activateFreeTrial", "Activate your free trial")
                      : t("enableSubscription", "Enable your subscription")}
                  </StepLabel>
                </Step>
                <Step>
                  <StepLabel>{t("allDone", "All done")}</StepLabel>
                </Step>
              </Stepper>
            </Grid>
            <Grid item sm={10}>
              <Card>
                <CardHeader
                  title={
                    setupStep === "ORGANIZATION"
                      ? t("setupOrg", "Setup your organization")
                      : setupStep === "SUBSCRIPTION"
                      ? hasTrial
                        ? t("activateFreeTrial", "Activate your free trial")
                        : t("enableSubscription", "Enable your subscription")
                      : ""
                  }
                />
                <CardContent>
                  {setupStep === "ORGANIZATION" && (
                    <Stack direction="column" spacing={3}>
                      <TextField
                        type="text"
                        value={organizationName}
                        label={t("orgName", "Organization Name")}
                        required
                        onChange={(event) => {
                          setOrganizationName(event.target.value);
                        }}
                        name="orgName"
                      inputProps={{ "data-testid": testIds.accountSetupModalOrganizationName } as React.InputHTMLAttributes<HTMLInputElement>}
                      />
                      <TextField
                        type="text"
                        value={dotNumber}
                        label={t("dotNumber", "DOT Number")}
                        onChange={(event) => {
                          setDotNumber(event.target.value);
                        }}
                        name="dotNumber"
                      inputProps={{ "data-testid": testIds.accountSetupModalDotNumber } as React.InputHTMLAttributes<HTMLInputElement>}
                      />
                    </Stack>
                  )}
                  {setupStep === "SUBSCRIPTION" && (
                    <Stack spacing={3}>
                      {hasTrial ? (
                        <>
                          <Typography variant="body2">
                            {isSubscribing
                              ? `${t(
                                  "activateFreeTrial",
                                  "Activating your trial"
                                )}...`
                              : t(
                                  "freeTrialDescription",
                                  "Choose the subscription plan that matches your business needs. You can cancel at any point in time."
                                )}
                          </Typography>
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>
                              {t("fleetPlan", "Fleet Plan")}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              paragraph
                            >
                              {t(
                                "fleetPlanDescription",
                                "Designed for fleet management operations with driver tracking, asset management, and trip planning features."
                              )}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>
                              {t("liquidPlan", "Liquid Plan")}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              paragraph
                            >
                              {t(
                                "liquidPlanDescription",
                                "Optimized for liquid bulk transportation with specialized features for tank management and liquid cargo tracking."
                              )}
                            </Typography>
                          </Box>
                        </>
                      ) : (
                        <Typography variant="body2">
                          {t(
                            "enableSubscriptionDescription",
                            "Enable your subscription in order to start using the platform."
                          )}
                        </Typography>
                      )}
                    </Stack>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: "space-between" }}>
                  {setupStep === "ORGANIZATION" && (
                    <LoadingButton data-testid={testIds.accountSetupModalNext}
                      onClick={() => {
                        onOrganizationSetup(organizationName, dotNumber);
                      }}
                      variant="contained"
                      loading={isSettingUpOrganization}
                      id="next-button"
                    >
                      {t("next", "Next")}
                    </LoadingButton>
                  )}
                  {setupStep === "SUBSCRIPTION" && hasTrial && (
                    <Stack direction="row" spacing={2}>
                      <LoadingButton data-testid={testIds.accountSetupModalStartFleetTrial}
                        onClick={() => {
                          onSubscribe("fleet");
                        }}
                        variant="contained"
                        loading={isSubscribing}
                        id="activate-fleet-trial-button"
                      >
                        {t("startFleetTrial", "Start Fleet Trial")}
                      </LoadingButton>
                      <LoadingButton data-testid={testIds.accountSetupModalStartLiquidTrial}
                        onClick={() => {
                          onSubscribe("liquid");
                        }}
                        variant="contained"
                        loading={isSubscribing}
                        id="activate-liquid-trial-button"
                      >
                        {t("startLiquidTrial", "Start Liquid Trial")}
                      </LoadingButton>
                    </Stack>
                  )}
                  {setupStep === "SUBSCRIPTION" && !hasTrial && (
                    <LoadingButton data-testid={testIds.accountSetupModalActivate}
                      onClick={() => {
                        onSubscribe("fleet"); // Default to fleet for existing subscriptions
                      }}
                      variant="contained"
                      loading={isSubscribing}
                      id="activate-button"
                    >
                      {t("activate", "Activate")}
                    </LoadingButton>
                  )}
                  <Button data-testid={testIds.accountSetupModalLogout} id="logout-button" onClick={() => signOut()}>
                    {t("common:logout", "Logout")}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </DialogContent>
    </Dialog>
  );
};

export default AccountSetupModal;
